import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { C, font, mono } from "../../../lib/styles.js";
import { ingest } from "../../../lib/ingestCallables.js";

/**
 * Roster import — bulk CSV/XLSX → rosterEntries.
 *
 * Flow:
 *   1. Operator picks a job (passed in via props) and drops a file.
 *   2. Client parses with SheetJS, maps producer columns → schema fields.
 *   3. Preview table shows what will land + what will skip (rows missing a
 *      first or last name). Operator hits Confirm.
 *   4. ingest.importRoster() callable creates the rosterEntries + qrSecrets.
 *   5. Result screen shows imported/skipped counts.
 *
 * Column mapping is locked to the producer-CSV format Ivin provided
 * (TGA S4 sample). Columns:
 *
 *   First Name, Last Name, Phone Number, Email Address, Union ID Number,
 *   Union, Gender, Role, EDBR Status
 */
export default function RosterImportView({ jobId, jobLabel, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);  // raw row dicts from xlsx
  const [headers, setHeaders] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [step, setStep] = useState("pick"); // pick | preview | sending | done
  const [result, setResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  // A11y: focus trap + escape-to-close + initial focus on mount.
  const cardRef = useRef(null);
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && cardRef.current) {
        const focusable = cardRef.current.querySelectorAll(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKey);
    const initial = cardRef.current?.querySelector(
      'input, select, textarea, button:not([aria-label="Close"]):not([disabled])',
    );
    initial?.focus();
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleFile = async (selected) => {
    if (!selected) return;
    setFile(selected);
    setParseError(null);
    try {
      const buf = await selected.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
      if (data.length === 0) throw new Error("Spreadsheet has no data rows.");
      setHeaders(Object.keys(data[0]));
      setRows(data);
      setStep("preview");
    } catch (err) {
      console.error("[RosterImport] parse failed", err);
      setParseError(err.message ?? String(err));
      setFile(null);
      setRows([]);
    }
  };

  // Map producer columns → schema rows.
  const mapped = useMemo(() => {
    const out = [];
    rows.forEach((r, rowIndex) => {
      const firstName = String(r["First Name"] ?? "").trim();
      const lastName = String(r["Last Name"] ?? "").trim();
      const edbr = String(r["EDBR Status"] ?? "").trim();
      const row = {
        rowIndex,
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        phone: String(r["Phone Number"] ?? "").trim() || null,
        email: String(r["Email Address"] ?? "").trim() || null,
        unionId: r["Union ID Number"] ?? null,
        union: String(r["Union"] ?? "").trim() || null,
        gender: String(r["Gender"] ?? "").trim() || null,
        role: String(r["Role"] ?? "").trim() || null,
        edbrStatus: edbr || null,
      };
      let skipReason = null;
      if (!firstName || !lastName) skipReason = "missing name";
      out.push({ ...row, skipReason });
    });
    return out;
  }, [rows]);

  const willImport = mapped.filter((r) => !r.skipReason);
  const willSkip = mapped.filter((r) => r.skipReason);

  const handleConfirm = async () => {
    setStep("sending");
    setSendError(null);
    try {
      // Send the FULL mapped set; backend re-applies the skip rules so the
      // audit row reflects what the operator selected, not the client's
      // pre-filtered slice. (Same pattern as the send-QRs flow.)
      const payload = mapped.map((r) => ({
        firstName: r.firstName,
        lastName: r.lastName,
        phone: r.phone,
        email: r.email,
        unionId: r.unionId,
        union: r.union,
        gender: r.gender,
        role: r.role,
        edbrStatus: r.edbrStatus,
      }));
      const res = await ingest.importRoster({ jobId, rows: payload });
      setResult(res);
      setStep("done");
      if (onImported) onImported(res);
    } catch (err) {
      console.error("[RosterImport] importRoster failed", err);
      setSendError(err.message ?? String(err));
      setStep("preview");
    }
  };

  return (
    <div style={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={cardRef}
        style={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-roster-title"
      >
        <button type="button" style={styles.close} onClick={onClose} aria-label="Close">×</button>

        <div style={styles.header}>
          <h2 id="import-roster-title" style={styles.title}>Import roster</h2>
          <div style={styles.subtitle}>{jobLabel ?? jobId}</div>
        </div>

        {step === "pick" && (
          <div style={styles.body}>
            <p style={styles.help}>
              Pick a producer CSV or XLSX file. Expected columns: First Name,
              Last Name, Phone Number, Email Address, Union ID Number, Union,
              Gender, Role, EDBR Status. Everyone in the file will be imported,
              including EDBR=Decline. Only rows missing a first or last name
              will be skipped.
            </p>
            <label style={styles.fileLabel}>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={{ display: "none" }}
              />
              <span style={styles.fileBtn}>Choose file…</span>
              {file && <span style={styles.fileName}>{file.name}</span>}
            </label>
            {parseError && <div style={styles.error}>{parseError}</div>}
            <div style={styles.actions}>
              <button type="button" style={styles.btnSecondary} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div style={styles.body}>
            <div style={styles.statRow}>
              <Stat label="In file" value={mapped.length} />
              <Stat label="Will import" value={willImport.length} />
              <Stat label="Will skip" value={willSkip.length} muted />
            </div>

            {willSkip.length > 0 && (
              <div style={styles.skipsBox}>
                <strong>Skipped:</strong>{" "}
                {willSkip.map((r) => `${r.name || "(no name)"} (${r.skipReason})`).join(" · ")}
              </div>
            )}

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Phone</th>
                    <th style={styles.th}>EDBR</th>
                  </tr>
                </thead>
                <tbody>
                  {mapped.map((r) => (
                    <tr key={r.rowIndex} style={r.skipReason ? styles.rowSkip : styles.row}>
                      <td style={styles.td}>{r.rowIndex + 1}</td>
                      <td style={styles.td}>
                        {r.name || <em style={styles.empty}>missing</em>}
                        {r.skipReason && (
                          <span style={styles.skipTag}>{r.skipReason}</span>
                        )}
                      </td>
                      <td style={styles.td}>{r.role || <span style={styles.muted}>—</span>}</td>
                      <td style={styles.tdMono}>{r.email || <span style={styles.muted}>—</span>}</td>
                      <td style={styles.tdMono}>{r.phone || <span style={styles.muted}>—</span>}</td>
                      <td style={styles.td}>{r.edbrStatus || <span style={styles.muted}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sendError && <div style={styles.error}>{sendError}</div>}

            <div style={styles.actions}>
              <button type="button" style={styles.btnSecondary} onClick={() => setStep("pick")}>← Back</button>
              <button
                type="button"
                style={willImport.length === 0 ? styles.btnDisabled : styles.btnPrimary}
                onClick={handleConfirm}
                disabled={willImport.length === 0}
              >
                Import {willImport.length} {willImport.length === 1 ? "subject" : "subjects"}
              </button>
            </div>
          </div>
        )}

        {step === "sending" && (
          <div style={styles.body}>
            <div style={styles.sending}>Importing… don't close this window.</div>
          </div>
        )}

        {step === "done" && result && (
          <div style={styles.body}>
            <div style={styles.statRow}>
              <Stat label="Imported" value={result.imported.length} />
              <Stat label="Skipped" value={result.skipped.length} muted />
            </div>
            <p style={styles.help}>
              Batch id: <span style={{ fontFamily: mono }}>{result.importBatchId}</span>
            </p>
            {result.skipped.length > 0 && (
              <div style={styles.skipsBox}>
                <strong>Skipped on server:</strong>{" "}
                {result.skipped.map((s) => `${s.name ?? "?"} (${s.reason})`).join(" · ")}
              </div>
            )}
            <div style={styles.actions}>
              <button type="button" style={styles.btnPrimary} onClick={onClose}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, muted }) {
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={muted ? styles.statValueMuted : styles.statValue}>{value}</div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    zIndex: 100,
  },
  modal: {
    background: C.surface,
    borderRadius: 14,
    boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
    maxWidth: 820,
    width: "100%",
    maxHeight: "calc(100vh - 64px)",
    overflowY: "auto",
    padding: 0,
    position: "relative",
    fontFamily: font,
  },
  close: {
    position: "absolute",
    top: 12,
    right: 16,
    background: "none",
    border: "none",
    fontSize: 22,
    color: C.muted,
    cursor: "pointer",
    lineHeight: 1,
    padding: "4px 8px",
  },
  header: {
    padding: "20px 28px 12px",
    borderBottom: `1px solid ${C.border}`,
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 500,
    color: C.text,
  },
  subtitle: {
    marginTop: 4,
    fontSize: 12,
    color: C.muted,
  },
  body: {
    padding: "20px 28px 24px",
  },
  help: {
    fontSize: 13,
    color: C.muted,
    lineHeight: 1.5,
    margin: "0 0 16px",
  },
  fileLabel: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    marginBottom: 16,
  },
  fileBtn: {
    background: C.text,
    color: "#fff",
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 500,
  },
  fileName: {
    fontSize: 13,
    color: C.muted,
    fontFamily: mono,
  },
  statRow: {
    display: "flex",
    gap: 24,
    padding: "16px 20px",
    background: C.surfaceAlt,
    borderRadius: 10,
    marginBottom: 16,
  },
  stat: {},
  statLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.muted,
    marginBottom: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: 500,
    color: C.text,
  },
  statValueMuted: {
    fontSize: 20,
    fontWeight: 500,
    color: C.muted,
  },
  skipsBox: {
    fontSize: 12,
    color: C.muted,
    background: C.surfaceAlt,
    padding: "10px 14px",
    borderRadius: 8,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  tableWrap: {
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: "auto",
    maxHeight: 320,
    marginBottom: 16,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    textAlign: "left",
    padding: "8px 12px",
    background: C.surfaceAlt,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: C.muted,
    fontWeight: 500,
    borderBottom: `1px solid ${C.border}`,
    position: "sticky",
    top: 0,
  },
  td: {
    padding: "8px 12px",
    borderBottom: `1px solid ${C.border}`,
  },
  tdMono: {
    padding: "8px 12px",
    borderBottom: `1px solid ${C.border}`,
    fontFamily: mono,
    fontSize: 11,
    color: C.muted,
  },
  row: {},
  rowSkip: {
    background: C.surfaceAlt,
    opacity: 0.7,
  },
  muted: { color: C.muted, fontStyle: "italic" },
  empty: { color: C.danger },
  skipTag: {
    display: "inline-block",
    marginLeft: 8,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: C.muted,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 3,
    padding: "1px 6px",
  },
  actions: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  btnPrimary: {
    background: C.text,
    color: "#fff",
    border: `1px solid ${C.text}`,
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnSecondary: {
    background: "transparent",
    color: C.text,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnDisabled: {
    background: C.surfaceAlt,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 18px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "not-allowed",
  },
  error: {
    background: "rgba(220, 38, 38, 0.06)",
    border: `1px solid ${C.danger}`,
    color: C.danger,
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 12,
    marginBottom: 16,
  },
  sending: {
    fontSize: 14,
    color: C.muted,
    textAlign: "center",
    padding: "32px 0",
  },
};
